import { BasicButton, Loading, OTP } from "@/components";
import PhoneNumber from "@/components/input/PhoneNumber";
import { STORAGE_KEYS } from "@/libraries/constant";
import { isAuthenticating } from "@/libraries/helper";
import { phoneNumber, verification } from "@/redux/reducers/auth";
import { PhoneNumbersSchema } from "@/schemas";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

export const Route = createFileRoute("/create-account/verify-mobile")({
	beforeLoad: () => {
		if (!isAuthenticating()) {
			throw redirect({
				to: "/create-account",
			});
		}
	},
	component: RouteComponent,
});

function RouteComponent() {
	const dispatch = useDispatch();
	const navigate = Route.useNavigate();
	const [number, setNumber] = useState({
		dial_code: "",
		country_name: "",
		phone_number: "",
		code: "",
	});
	const phone = useSelector((state: any) => state.auth?.phoneNumber);
	const messageOTP = useSelector((state: any) => state.auth?.Verification);
	const [verify_code, setVerify_Code] = useState("");
	const [alertMessage, setAlertMessage] = useState("");
	const [buttonDisable, setButtonDisable] = useState(true);
	const [disable, setIsDisabled] = useState(false);
	const [loading, setLoading] = useState(false);

	const sendDisable = useMemo(() => {
		const phoneNumber = number.dial_code + number.phone_number;
		return PhoneNumbersSchema.safeParse(phoneNumber);
	}, [number.dial_code, number.phone_number]);

	// Update button states and handle error messages
	useEffect(() => {
		setButtonDisable(!(number && verify_code));
		if (phone?.status === "failed" || messageOTP?.status === "failed") {
			setLoading(false);
			if (phone?.error) {
				setAlertMessage(phone.error?.errors);
			} else if (messageOTP?.error) {
				setAlertMessage(messageOTP?.error?.messages);
			}
		}

		if (phone?.data?.otp) {
			setLoading(false);
			setVerify_Code(phone?.data?.otp);
		}
	}, [number, verify_code, messageOTP, phone]);

	// send phone number to api
	const handlePhoneNumber = useCallback(() => {
		setLoading(true);
		dispatch(
			phoneNumber({
				phone_number: number.dial_code + number.phone_number,
				callback: () => {
					setLoading(false);
					setAlertMessage("");
					return toast.success(
						"Your OTP has been sent to your registered mobile number.",
					);
				},
			}),
		);
	}, [dispatch, number]);

	// verify phone number
	const handleVerify = useCallback(() => {
		setLoading(true);
		const isVerified = !!localStorage.getItem(
			STORAGE_KEYS.FORM_VERIFY_MOBILE_VERIFY_CODE,
		);
		if (isVerified) {
			return navigate({
				to: "/create-account/professional-credentials",
			});
		}
		dispatch(
			verification({
				verify_code: verify_code,
				callback: () => {
					setLoading(false);
					setAlertMessage("");
					localStorage.setItem(
						STORAGE_KEYS.FORM_VERIFY_MOBILE_PHONE_NUMBER,
						JSON.stringify({ ...number }),
					);
					localStorage.setItem(
						STORAGE_KEYS.FORM_VERIFY_MOBILE_VERIFY_CODE,
						JSON.stringify({ verify_code }),
					);
					navigate({
						to: "/create-account/professional-credentials",
					});
				},
			}),
		);
	}, [dispatch, navigate, number, verify_code]);

	useEffect(() => {
		const storedVerifyCode = JSON.parse(
			localStorage.getItem(STORAGE_KEYS.FORM_VERIFY_MOBILE_VERIFY_CODE) || "{}",
		);
		if (
			storedVerifyCode &&
			Object.keys(storedVerifyCode).length > 0 &&
			storedVerifyCode.verify_code !== ""
		) {
			setIsDisabled(true);
			setVerify_Code(storedVerifyCode.verify_code);
		}
	}, []);

	return (
		<>
			<div className="py-4 relative container flex-1 overflow-y-auto">
				<div className="py-4">
					<PhoneNumber
						value={number.phone_number}
						errorMessage={alertMessage}
						onChange={(e) =>
							setNumber({
								phone_number: e.phone_number,
								dial_code: e.dial_code,
								country_name: e.country_name,
								code: e.code,
							})
						}
					/>
				</div>
				<BasicButton
					variant="contained"
					color="secondary"
					disable={!sendDisable.success}
					onClick={handlePhoneNumber}
					className="w-full !h-12 text-sm font-semibold !mb-6"
				>
					SEND
				</BasicButton>
				<p className="text-dark text-sm mb-6">
					You will receive a 4-digit code via SMS to your mobile phone.
				</p>
				<p className="text-dark text-sm">
					Did not receive a code? Check your phone number and send again.
				</p>
				<div className="flex justify-center">
					<OTP
						separator={<span> </span>}
						value={verify_code}
						onChange={setVerify_Code}
						disable={disable}
						length={4}
					/>
				</div>
				<p className="text-dark text-sm">
					Insert your SMS code to verify your mobile device.
				</p>
			</div>
			<div className="container py-4 shadow">
				<BasicButton
					variant="contained"
					color="secondary"
					onClick={handleVerify}
					disabled={buttonDisable}
					loading={loading}
					className="w-full !h-12 text-sm font-semibold !shadow-none"
				>
					Next
				</BasicButton>
			</div>
			<Loading status={loading} />
		</>
	);
}
